import React, { useState, useEffect } from "react";
import { navigate } from "@reach/router";
import { Formik, Form } from "formik";
import styled from "@emotion/styled";
import { string, object, date } from "yup";
import { subYears, addSeconds } from "date-fns";
import { useCookies } from "react-cookie";
import isEmpty from "lodash/isEmpty";
import { TextField, DatePickerField, RadioField, SelectField } from "@components/common/molecules";
import { Button, H3, Text, Box, Row, Column, Link } from "@atoms";
import { RailsUrl, constants, spelling } from "@services";
import { useStoreAppValue } from "@hooks";
import { BeneficiaryTeamType, PaymentType, Currency, Country } from "@types";
import { BeneficiaryOrganization } from "../BeneficiaryOrganization";
import {
  CampaignBeneficiaryQuery_Campaign_beneficiary,
  CampaignBeneficiaryQuery_Campaign_organization,
  CampaignBeneficiaryQuery_Campaign_category,
} from "../BeneficiaryForm/__generated__/CampaignBeneficiaryQuery";
import { BeneficiaryOrgPreview } from "../BeneficiaryOrgPreview";

// == Types ================================================================

type TTeamType = "" | BeneficiaryTeamType;
interface IProps {
  beneficiary: CampaignBeneficiaryQuery_Campaign_beneficiary;
  category: CampaignBeneficiaryQuery_Campaign_category;
  organization: CampaignBeneficiaryQuery_Campaign_organization;
}

export interface IGooglePlace {
  placeId: string;
}

// == Constants ============================================================

const INITIAL_VALUES = {
  teamType: "",
  teamName: "",
  taxId: "",
  website: "",
  dateOfBirth: "",
  firstName: "",
  lastName: "",
  realTeamName: "",
  paymentType: PaymentType.HW_BANK_TRANSFER,
};

const VALIDATION_SCHEMA = object().shape({
  paymentType: string().required("Payment delivery method is required"),
  teamType: string().required("Team type is required"),
  realTeamName: string()
    .required("Team name is required")
    .max(
      constants.BENEFICIARY_VALIDATIONS.MAX_REAL_TEAM_NAME,
      `Please do not enter more than ${constants.BENEFICIARY_VALIDATIONS.MAX_REAL_TEAM_NAME} characters`
    )
    .matches(/^[a-zA-Z0-9\s!&'()+,-./:;]+$/, {
      message: "Please only include ! & ' ( ) + , - . / : ; as special characters.",
    }),
  taxId: string()
    .typeError("Required")
    .when("currency", {
      is: Currency.USD,
      then: string().matches(/^\d{2}-\d{7}$/, {
        message: "EIN number should be in the format 12-1234567",
        excludeEmptyString: true,
      }),
    })
    .when("currency", {
      is: Currency.CAD,
      then: string().matches(/^([0-9]{9}[R]{2}[0-9]{4})$/, {
        message: "Please enter a valid charity registration number",
        excludeEmptyString: true,
      }),
    })
    .nullable(),
  teamName: string().when("teamType", {
    is: BeneficiaryTeamType.ORGANIZATION,
    then: string().required("Organization name is required"),
  }),
  website: string()
    .typeError("Please enter a website")
    .when("teamType", {
      is: BeneficiaryTeamType.ORGANIZATION,
      then: string()
        .trim()
        .matches(/^(?:(ftp|http|https)?:\/\/)?(?:[\w-]+\.)+([a-z]|[A-Z]|[0-9]){2,6}(\/(.)*)?$/, {
          message: "Please enter a valid url",
          excludeEmptyString: true,
        }),
    })
    .nullable(),
  dateOfBirth: date()
    .typeError("Please enter a birth date")
    .when("teamType", {
      is: BeneficiaryTeamType.INDIVIDUAL,
      then: date()
        .required("Please select a valid date.")
        .max(subYears(new Date(), 18), "Must be 18 years or older to be a beneficiary"),
    })
    .nullable(),
  firstName: string()
    .typeError("Please enter a first name")
    .max(
      constants.BENEFICIARY_VALIDATIONS.MAX_FIRST_NAME,
      `Please do not enter more than ${constants.BENEFICIARY_VALIDATIONS.MAX_FIRST_NAME} characters`
    )
    .when("teamType", {
      is: BeneficiaryTeamType.INDIVIDUAL,
      then: string()
        .matches(/^[A-Za-z\s',-.]+$/, { message: "Please enter a valid first name" })
        .required("Please enter a first name"),
    })
    .nullable(),
  lastName: string()
    .typeError("Please enter a last name")
    .max(
      constants.BENEFICIARY_VALIDATIONS.MAX_LAST_NAME,
      `Please do not enter more than ${constants.BENEFICIARY_VALIDATIONS.MAX_LAST_NAME} characters`
    )
    .when("teamType", {
      is: BeneficiaryTeamType.INDIVIDUAL,
      then: string()
        .matches(/^[A-Za-z\s',-.]+$/, { message: "Please enter a valid last name" })
        .required("Please enter a last name"),
    })
    .nullable(),
  orgStreet: string()
    .when("teamType", {
      is: BeneficiaryTeamType.ORGANIZATION || BeneficiaryTeamType.CHARITY,
      then: string().required("Please enter organization address"),
    })
    .nullable(),
  orgCity: string()
    .when("teamType", {
      is: BeneficiaryTeamType.ORGANIZATION || BeneficiaryTeamType.CHARITY,
      then: string().required("Please enter organization city"),
    })
    .nullable(),
  orgProvince: string()
    .when("teamType", {
      is: BeneficiaryTeamType.ORGANIZATION || BeneficiaryTeamType.CHARITY,
      then: string().required("Please enter organization province"),
    })
    .nullable(),
  orgPostalCode: string()
    .trim()
    .when("teamType", {
      is: BeneficiaryTeamType.ORGANIZATION || BeneficiaryTeamType.CHARITY,
      then: string().required("ZIP/Postal code is required"),
    })
    .when("orgCountry", {
      is: Country.USA && (BeneficiaryTeamType.ORGANIZATION || BeneficiaryTeamType.CHARITY),
      then: string().matches(/(^\d{5}$)|(^\d{5}-\d{4}$)/, {
        message: "Must be in format 12345",
        excludeEmptyString: true,
      }),
    })
    .when("orgCountry", {
      is: Country.CANADA && (BeneficiaryTeamType.ORGANIZATION || BeneficiaryTeamType.CHARITY),
      then: string().matches(/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/, {
        message: "Must be in format L1L 4L4",
        excludeEmptyString: true,
      }),
    })
    .nullable(),
});

// const legalNameLabel = (teamType: TTeamType) => {
//   switch (teamType) {
//     case BeneficiaryTeamType.INDIVIDUAL:
//       return "Beneficiary Name";
//     case BeneficiaryTeamType.CHARITY:
//       return "Registered Charity / Non-Profit Name";
//     case BeneficiaryTeamType.ORGANIZATION:
//       return "Registered Team Name";
//     default:
//       return "Team Name";
//   }
// };

const CHECK_TYPES = (currency) => {
  return [
    [PaymentType.HW_BANK_TRANSFER, "Bank Transfer - Sent within 3-5 business days"],
    currency === Currency.USD
      ? [
          PaymentType.POSTAGE,
          `${spelling("checks", {
            country: `${currency === Currency.USD ? "USA" : "Canada"}`,
            capitalize: true,
            singular: true,
          })} - Sent to mailing address within 10-15 business days`,
        ]
      : [
          PaymentType.POSTAGE,
          `${spelling("checks", {
            country: `${currency === Currency.USD ? "USA" : "Canada"}`,
            capitalize: true,
            singular: true,
          })} - Sent to mailing address within 10-15 business days`,
        ],
  ];
};

const ORG_PLACEHOLDER = "Club / League / School / Non-Profit Organization Name";

BeneficiaryType.defaultProps = {};

// == Component ============================================================

export default function BeneficiaryType({ beneficiary, category, organization }: IProps) {
  const { currency, campaignId } = useStoreAppValue();
  const isCanada = currency === "CAD";
  const [cookies, setCookie, removeCookie] = useCookies(["beneficiaryType", "beneficiaryOrg"]);
  const [googlePlace, setGooglePlace] = useState<IGooglePlace | null>();
  const [manualOrgEntry, setManualOrgEntry] = useState(false);

  const onSubmit = (values) => {
    if (values.teamType === BeneficiaryTeamType.INDIVIDUAL) {
      const formattedDOB = values.dateOfBirth !== "" ? values.dateOfBirth : "";
      const input = {
        ...INITIAL_VALUES,
        teamType: values.teamType,
        realTeamName: values.realTeamName,
        firstName: values.firstName,
        lastName: values.lastName,
        dateOfBirth: formattedDOB,
        legalName: values.realTeamName,
        paymentType: values.paymentType,
      };
      setCookie("beneficiaryType", input, { expires: addSeconds(new Date(), 3600) });
    }
    if (
      values.teamType === BeneficiaryTeamType.ORGANIZATION ||
      values.teamType === BeneficiaryTeamType.CHARITY
    ) {
      const input =
        values.taxId === "" || values.taxId === null
          ? {
              ...INITIAL_VALUES,
              teamType: BeneficiaryTeamType.ORGANIZATION,
              realTeamName: values.realTeamName,
              teamName: values.teamName,
              website: values.website,
              legalName: values.realTeamName,
              paymentType: values.paymentType,
              googlePlacesId: values.googlePlacesId,
            }
          : {
              ...INITIAL_VALUES,
              teamType: BeneficiaryTeamType.CHARITY,
              realTeamName: values.realTeamName,
              taxId: values.taxId,
              legalName: values.realTeamName,
              paymentType: values.paymentType,
              googlePlacesId: values.googlePlacesId,
            };
      setCookie("beneficiaryType", input, { expires: addSeconds(new Date(), 3600) });
      const orgInput = {
        name: values.teamName,
        legalName: values.realTeamName,
        street1: values.orgStreet,
        city: values.orgCity,
        province: values.orgProvince,
        postalCode: values.orgPostalCode,
        country: values.orgCountry,
        categoryId: values.orgCategory,
      };

      setCookie("beneficiaryOrg", orgInput, { expires: addSeconds(new Date(), 3600) });
    }
    // if (values.teamType === BeneficiaryTeamType.CHARITY) {
    //   const input = {
    //     ...INITIAL_VALUES,
    //     teamType: values.teamType,
    //     realTeamName: values.realTeamName,
    //     taxId: values.taxId,
    //     legalName: values.realTeamName,
    //     paymentType: values.paymentType,
    //   };
    //   setCookie("beneficiaryType", input, { expires: addSeconds(new Date(), 3600) });
    //   const orgInput = {
    //     name: values.orgName,
    //     legalName: values.realTeamName,
    //     street1: values.orgStreet,
    //     city: values.orgCity,
    //     province: values.orgProvince,
    //     postalCode: values.orgPostalCode,
    //     country: currency === Country.USA ? Country.USA : Country.CANADA,
    //     categoryId: category.id,
    //   };
    //   setCookie("beneficiaryOrg", orgInput, { expires: addSeconds(new Date(), 3600) });
    // }
    navigate(RailsUrl.newBeneficiaryUrl(campaignId));
  };

  const taxIdText = currency === "CAD" ? "charity registration number" : "EIN number (12-1234567)";
  const taxIdLabel =
    currency === "CAD"
      ? "Charity registration number (123456789RR0001)"
      : "EIN number (12-1234567)";
  const taxIdPlaceholder = currency === "CAD" ? "Format: 123456789RR0001" : "EIN Format: 12-123456";

  const cookieBeneficiaryOrg = !cookies.beneficiaryOrg
    ? null
    : {
        orgStreet: cookies.beneficiaryOrg.street1,
        orgCity: cookies.beneficiaryOrg.city,
        orgPostalCode: cookies.beneficiaryOrg.postalCode,
        orgProvince: cookies.beneficiaryOrg.province,
        teamName: cookies.beneficiaryOrg.name,
      };
  const orgInputs = !organization
    ? null
    : {
        orgStreet: organization.street1,
        orgCity: organization.city,
        orgPostalCode: organization.postalCode,
        orgProvince: organization.province,
        teamName: organization.name,
      };

  const initialInputValues = {
    ...INITIAL_VALUES,
    ...(cookies.beneficiaryType || beneficiary || null),
    ...(cookieBeneficiaryOrg || orgInputs),
    paymentType: PaymentType.HW_BANK_TRANSFER,
  };

  // const onTeamTypeClick = (e, setFieldValue, resetForm) => {
  //   const nextInitialState = {
  //     ...INITIAL_VALUES,
  //     teamType: e.target.value,
  //   };
  //   resetForm(nextInitialState);
  //   setFieldValue("teamType", e.target.value);
  // };

  const onIsOrganizationClick = (e, setFieldValue, resetForm) => {
    const nextInitialState = {
      ...INITIAL_VALUES,
      teamType: e.target.value,
    };
    resetForm(nextInitialState);
    if (e.target.value === BeneficiaryTeamType.CHARITY) {
      setFieldValue("teamType", BeneficiaryTeamType.ORGANIZATION);
    }
    setFieldValue("teamType", e.target.value);
  };

  // const onClickSearchEntry = () => {
  //   setManualOrgEntry(false);
  // };

  useEffect(() => {
    if (!!cookies.beneficiaryOrg && !cookies.beneficiaryType?.googlePlacesId) {
      setManualOrgEntry(true);
    }
    if (!!organization && !beneficiary?.googlePlacesId) {
      setManualOrgEntry(true);
    }
  }, [beneficiary, cookies.beneficiaryOrg, cookies.beneficiaryType, organization]);

  const initialValues = () => {
    const values = {
      ...initialInputValues,
      currency,
      orgCategory: category?.id,
      orgCountry: isCanada ? Country.CANADA : Country.USA,
    };
    Object.keys(values).map((key) => {
      if (values[key] === null) {
        values[key] = "";
      }
    });
    return values;
  };
  return (
    <Formik
      initialValues={initialValues()}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={VALIDATION_SCHEMA}
      onSubmit={(values) => onSubmit(values)}
    >
      {({ values, resetForm, setFieldValue, errors }) => {
        return (
          <Form>
            <H3 as="p" color="black" fontWeight="bold" mb={2} mt={3}>
              Organization
            </H3>
            <Text as="p" mb={3}>
              Is your team part of a club / league / school / non-profit?
            </Text>
            {values.teamType === BeneficiaryTeamType.CHARITY ? (
              <RadioField
                label="Yes"
                name="teamType"
                value="CHARITY"
                onClick={(e) => onIsOrganizationClick(e, setFieldValue, resetForm)}
              />
            ) : (
              <RadioField
                label="Yes"
                name="teamType"
                value="ORGANIZATION"
                onClick={(e) => onIsOrganizationClick(e, setFieldValue, resetForm)}
              />
            )}
            <RadioField
              label="No"
              name="teamType"
              value="INDIVIDUAL"
              onClick={(e) => onIsOrganizationClick(e, setFieldValue, resetForm)}
            />
            {values.teamType !== "" && (
              <>
                {values.teamType !== BeneficiaryTeamType.INDIVIDUAL && (
                  <>
                    <TextField
                      // label={legalNameLabel(values.teamType)}
                      label="Team"
                      name="realTeamName"
                      placeholder="Registered Team Name"
                    />
                    {!manualOrgEntry && (
                      <BeneficiaryOrganization
                        organizationName={organization?.name}
                        orgPlaceholder={ORG_PLACEHOLDER}
                        setFieldValue={setFieldValue}
                        setGooglePlace={setGooglePlace}
                        setManualOrgEntry={setManualOrgEntry}
                      />
                    )}
                  </>
                )}
                {values.teamType !== BeneficiaryTeamType.INDIVIDUAL ? (
                  <>
                    {manualOrgEntry && (
                      <>
                        <TextField
                          label="Organization"
                          name="teamName"
                          placeholder={ORG_PLACEHOLDER}
                        />
                        {/* <ELink
                          display="block"
                          mt="-18px"
                          mb={2}
                          fontSize={0}
                          onClick={onClickSearchEntry}
                        >
                          Search organization.
                        </ELink> */}
                        <TextField
                          disabled={!manualOrgEntry}
                          label="Organization Address"
                          name="orgStreet"
                          placeholder="Address"
                        />
                        <Row>
                          <Column>
                            <TextField
                              disabled={!manualOrgEntry}
                              name="orgCity"
                              placeholder="City"
                            />
                          </Column>
                          <Column>
                            <TextField disabled name="orgCountry" placeholder="Country" />
                          </Column>
                        </Row>
                        <Row>
                          <Column>
                            {manualOrgEntry ? (
                              <>
                                {!isCanada ? (
                                  <SelectField name="orgProvince">
                                    <>
                                      <option value="">State</option>
                                      {constants.STATES.map((state) => {
                                        return (
                                          <option key={state[0]} value={state[0]}>
                                            {state[1]}
                                          </option>
                                        );
                                      })}
                                    </>
                                  </SelectField>
                                ) : (
                                  <SelectField name="orgProvince">
                                    <>
                                      <option value="">Province</option>
                                      {constants.PROVINCES.map((province) => {
                                        return (
                                          <option key={province[0]} value={province[0]}>
                                            {province[1]}
                                          </option>
                                        );
                                      })}
                                    </>
                                  </SelectField>
                                )}
                              </>
                            ) : (
                              <TextField
                                disabled
                                name="orgProvince"
                                placeholder={isCanada ? "Province" : "State"}
                              />
                            )}
                          </Column>

                          <Column>
                            <TextField
                              disabled={!manualOrgEntry}
                              name="orgPostalCode"
                              placeholder={isCanada ? "Postal Code" : "Zip Code"}
                            />
                          </Column>
                        </Row>
                        <TextField
                          label="Organization Website"
                          name="website"
                          placeholder="https://www.organization-name.com "
                        />
                      </>
                    )}
                    {(beneficiary?.googlePlacesId || values.googlePlacesId) && !manualOrgEntry && (
                      <>
                        <BeneficiaryOrgPreview
                          orgAddress={values.orgStreet ? values.orgStreet : organization?.street1}
                          orgCity={values.orgCity ? values.orgCity : organization?.city}
                          orgCountry={values.orgCountry ? values.orgCountry : organization?.country}
                          orgName={values.teamName ? values.teamName : organization?.name}
                          orgPostalCode={
                            values.orgPostalCode ? values.orgPostalCode : organization?.postalCode
                          }
                          orgProvince={
                            values.orgProvince ? values.orgProvince : organization?.province
                          }
                          orgWebsite={values.website ? values.website : beneficiary?.website}
                        />
                      </>
                    )}
                    {!isEmpty(errors) &&
                      !manualOrgEntry &&
                      (errors?.teamName ||
                        errors?.orgCity ||
                        errors?.orgPostalCode ||
                        errors?.orgProvince ||
                        errors?.orgStreet) && (
                        <Text color="danger" display="block" fontSize="0.8rem" mb={2} mt="-12px">
                          Please enter your organization.
                        </Text>
                      )}
                    <Text as="p" mb={3}>
                      Is your organization a registered Charity / Non-Profit? Please provide your{" "}
                      {taxIdText}:
                    </Text>
                    <TextField label={taxIdLabel} name="taxId" placeholder={taxIdPlaceholder} />
                  </>
                ) : (
                  <>
                    <TextField
                      label="First Name"
                      name="firstName"
                      placeholder="First Name"
                      onChange={(e) => {
                        setFieldValue("realTeamName", `${e.target.value} ${values.lastName}`);
                      }}
                    />
                    <TextField
                      label="Last Name"
                      name="lastName"
                      placeholder="Last Name"
                      onChange={(e) => {
                        setFieldValue("realTeamName", `${values.firstName} ${e.target.value}`);
                      }}
                    />
                    <DatePickerField
                      showYearDropdown
                      label="Date of Birth"
                      maxDate={subYears(new Date(), 18)}
                      minDate={null}
                      name="dateOfBirth"
                      placeholder="YYYY-MM-DD"
                    />
                  </>
                )}
              </>
            )}
            <H3 as="p" color="black" fontWeight="bold" mb={2} mt={3}>
              Payment Method
            </H3>
            <Text as="p" mb={3}>
              Our standard method of payment is via bank transfer, you will be asked to login to
              your online bank account on the next step to verify. Payment by{" "}
              {spelling("checks", {
                country: `${currency === "USD" ? "USA" : "Canada"}`,
                capitalize: false,
                singular: true,
              })}{" "}
              is also available.
            </Text>
            <SelectField
              label="Payment Delivery Method"
              name="paymentType"
              placeholder="Please Select"
            >
              {CHECK_TYPES(currency).map((type) => {
                return (
                  <option key={type[0]} value={type[0]}>
                    {type[1]}
                  </option>
                );
              })}
            </SelectField>
            <Box textAlign="center">
              <Button mt={3} type="onSubmit" width={0.8}>
                Next
              </Button>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
}

// == Styles ===============================================================

const ELink = styled(Link)`
  position: relative;
  z-index: 10;
`;
